import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Keeps first-order gift lines (line property `_blush_gift`, added by the product page)
 * tied to a subscription: removes them when no line in the cart has a selling plan, holds
 * each gift at a quantity of one, and re-applies the gift code (`_blush_gift_code`) if it
 * was removed. Gifts the cart still charges for are removed rather than paid for.
 *
 * @typedef {{ key: string, variant_id: number, quantity: number, final_line_price: number, selling_plan_allocation?: object | null, properties?: Record<string, string> | null }} CartLine
 * @typedef {{ items: CartLine[], discount_codes?: Array<{ code: string }> }} Cart
 */

const SOURCE = 'blush-gift-guard';
let running = false;

/**
 * @param {Record<string, unknown>} body
 * @returns {Promise<Cart & { sections?: Record<string, string> }>}
 */
async function updateCart(body) {
  const response = await fetch(`${Theme.routes.cart_update_url}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const updated = await response.json();
  if (!response.ok || updated.status) throw new Error(updated.description || 'Cart update failed');
  return updated;
}

async function enforceGiftRules() {
  if (running) return;
  running = true;

  try {
    /** @type {Cart} */
    let cart = await fetch(`${Theme.routes.cart_url}.js`).then((res) => res.json());
    let giftLines = cart.items.filter((line) => line.properties?._blush_gift);
    if (!giftLines.length) return;

    const qualifies = cart.items.some((line) => line.selling_plan_allocation);
    /** @type {Record<string, number>} */
    const updates = {};
    /** @type {string | undefined} */
    let discount;
    const seen = new Set();
    for (const line of giftLines) {
      if (!qualifies || seen.has(line.variant_id)) updates[line.key] = 0;
      else if (line.quantity !== 1) updates[line.key] = 1;
      seen.add(line.variant_id);
    }

    if (qualifies && giftLines.some((line) => line.final_line_price > 0)) {
      const giftCode = giftLines.find((line) => line.properties?._blush_gift_code)?.properties?._blush_gift_code;
      const codes = (cart.discount_codes ?? []).map((applied) => applied.code);
      if (giftCode && !codes.some((code) => code.toLowerCase() === giftCode.toLowerCase())) {
        // Check the code actually frees the gifts before keeping them
        cart = await updateCart({ discount: [...codes, giftCode].join(',') });
        discount = [...codes, giftCode].join(',');
        giftLines = cart.items.filter((line) => line.properties?._blush_gift);
      }
      for (const line of giftLines) {
        if (line.final_line_price > 0) updates[line.key] = 0;
      }
    }
    if (!Object.keys(updates).length && !discount) return;

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    document.documentElement.dispatchEvent(
      new CartLinesUpdateEvent({
        action: qualifies ? 'update' : 'remove',
        context: 'cart',
        lines: Object.entries(updates).map(([id, quantity]) => ({ id, quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const updated = await updateCart({ updates, sections: sectionIds.join(',') });

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(updated),
        detail: { didError: false, items: updated.items, sections: updated.sections, source: SOURCE },
      });
    } catch (error) {
      deferred.reject(error);
    }
  } catch (error) {
    console.error(error);
  } finally {
    running = false;
  }
}

document.addEventListener(StandardEvents.cartLinesUpdate, async (event) => {
  const { promise } = /** @type {CartLinesUpdateEvent} */ (event);
  if (!promise) return;
  try {
    const { detail } = await promise;
    if (detail?.source === SOURCE) return;
  } catch {
    // A failed update can still have changed the cart
  }
  enforceGiftRules();
});

// The cart can also change without a cart event (another tab, a full page cart update)
if (document.querySelector('script[data-blush-gift-guard][data-cart-has-gifts]')) enforceGiftRules();
